import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type {
  ClassDiscordConfig,
  ClassReminderSlot,
  ClassReminderException,
  PortalClass,
} from '../../types/portal';
import { Check, Bell, Plus, Trash2, Calendar, Search, ChevronDown, ChevronRight, Send } from 'lucide-react';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm disabled:opacity-50';
const secondaryBtn =
  'inline-flex items-center px-3 py-1.5 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';

const WEEKDAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Montag' },
  { value: 2, label: 'Dienstag' },
  { value: 3, label: 'Mittwoch' },
  { value: 4, label: 'Donnerstag' },
  { value: 5, label: 'Freitag' },
  { value: 6, label: 'Samstag' },
  { value: 7, label: 'Sonntag' },
];

type ConfigForm = {
  webhook_url: string;
  mention: string;
  lead_minutes: number;
  is_enabled: boolean;
};

const emptyConfig = (): ConfigForm => ({
  webhook_url: '',
  mention: '',
  lead_minutes: 15,
  is_enabled: true,
});

const ReminderSettingsManager: React.FC = () => {
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [configs, setConfigs] = useState<Record<string, ConfigForm>>({});
  const [slots, setSlots] = useState<Record<string, ClassReminderSlot[]>>({});
  const [exceptions, setExceptions] = useState<Record<string, ClassReminderException[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [testState, setTestState] = useState<Record<string, 'sending' | 'ok' | 'err'>>({});
  const [error, setError] = useState('');

  // Entwurf für neue Ausnahme pro Klasse
  const [exDraft, setExDraft] = useState<Record<string, { date: string; status: 'cancelled' | 'postponed'; note: string }>>({});
  // Ausfall/Verschiebung-Formular pro Klasse ein-/ausklappen (nur bei Bedarf)
  const [openEx, setOpenEx] = useState<Record<string, boolean>>({});
  // Klassen-Karten ein-/ausklappen (standardmäßig zu) + Suche
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const [{ data: cData }, { data: cfgData }, { data: slotData }, { data: exData }] = await Promise.all([
      supabase.from('portal_classes').select('*').eq('is_active', true).order('title'),
      supabase.from('class_discord_config').select('*'),
      supabase.from('class_reminder_slots').select('*').order('weekday').order('start_time'),
      supabase.from('class_reminder_exceptions').select('*').order('exception_date'),
    ]);

    const cfgMap: Record<string, ConfigForm> = {};
    (cfgData as ClassDiscordConfig[] | null)?.forEach(cfg => {
      cfgMap[cfg.class_id] = {
        webhook_url: cfg.webhook_url ?? '',
        mention: cfg.mention ?? '',
        lead_minutes: cfg.lead_minutes ?? 15,
        is_enabled: cfg.is_enabled,
      };
    });

    const slotMap: Record<string, ClassReminderSlot[]> = {};
    (slotData as ClassReminderSlot[] | null)?.forEach(s => {
      (slotMap[s.class_id] ??= []).push(s);
    });

    const exMap: Record<string, ClassReminderException[]> = {};
    (exData as ClassReminderException[] | null)?.forEach(e => {
      (exMap[e.class_id] ??= []).push(e);
    });

    setClasses((cData ?? []) as PortalClass[]);
    setConfigs(cfgMap);
    setSlots(slotMap);
    setExceptions(exMap);
    setLoading(false);
  };

  const cfgOf = (classId: string): ConfigForm => configs[classId] ?? emptyConfig();
  const updateCfg = (classId: string, patch: Partial<ConfigForm>) =>
    setConfigs(prev => ({ ...prev, [classId]: { ...cfgOf(classId), ...patch } }));

  // „Aktiv"-Schalter sofort speichern (keine extra „Speichern"-Klick nötig).
  // Greift nur, wenn für die Klasse bereits ein Webhook gespeichert ist –
  // sonst bleibt der Wert lokal, bis der Webhook gespeichert wird.
  const toggleEnabled = async (classId: string, value: boolean) => {
    updateCfg(classId, { is_enabled: value });
    await supabase
      .from('class_discord_config')
      .update({ is_enabled: value, updated_at: new Date().toISOString() })
      .eq('class_id', classId);
  };

  const saveConfig = async (classId: string) => {
    const cfg = cfgOf(classId);
    setError('');
    if (!cfg.webhook_url.trim()) {
      setError('Bitte zuerst eine Discord-Webhook-URL eintragen.');
      return;
    }
    setSavingId(classId);
    try {
      const { error: err } = await supabase.from('class_discord_config').upsert({
        class_id: classId,
        webhook_url: cfg.webhook_url.trim(),
        mention: cfg.mention.trim() || null,
        lead_minutes: cfg.lead_minutes,
        is_enabled: cfg.is_enabled,
        updated_at: new Date().toISOString(),
      });
      if (err) throw err;
      setSavedId(classId);
      setTimeout(() => setSavedId(s => (s === classId ? null : s)), 2000);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSavingId(null);
    }
  };

  // Testnachricht serverseitig über die DB (pg_net) senden – exakt der
  // Weg, den auch die echten Erinnerungen nehmen. Kein Browser-/CORS-Problem.
  // Voraussetzung: Webhook der Klasse ist gespeichert.
  const sendTest = async (classId: string) => {
    setError('');
    setTestState(prev => ({ ...prev, [classId]: 'sending' }));
    try {
      const { error: err } = await supabase.rpc('send_test_reminder', { p_class_id: classId });
      if (err) throw err;
      setTestState(prev => ({ ...prev, [classId]: 'ok' }));
      setTimeout(() => setTestState(prev => {
        const next = { ...prev };
        delete next[classId];
        return next;
      }), 5000);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      const msg = (err as { message?: string })?.message ?? '';
      setError(
        msg.includes('Webhook')
          ? 'Für diese Klasse ist noch kein Webhook gespeichert. Bitte zuerst „Webhook speichern".'
          : 'Test fehlgeschlagen: ' + (msg || 'unbekannter Fehler')
      );
      setTestState(prev => ({ ...prev, [classId]: 'err' }));
    }
  };

  // ─── Wochen-Slots ───────────────────────────────────────────
  const addSlot = async (classId: string) => {
    setError('');
    const { data, error: err } = await supabase
      .from('class_reminder_slots')
      .insert({ class_id: classId, weekday: 2, start_time: '19:00' })
      .select()
      .single();
    if (err) {
      setError('Slot konnte nicht angelegt werden.');
      return;
    }
    setSlots(prev => ({ ...prev, [classId]: [...(prev[classId] ?? []), data as ClassReminderSlot] }));
  };

  const updateSlot = async (classId: string, slotId: string, patch: Partial<Pick<ClassReminderSlot, 'weekday' | 'start_time'>>) => {
    // Bei Änderung von Tag/Uhrzeit den „heute schon erinnert"-Marker
    // zurücksetzen, damit der neue Termin heute noch feuern kann.
    const dbPatch = { ...patch, last_reminded_on: null };
    setSlots(prev => ({
      ...prev,
      [classId]: (prev[classId] ?? []).map(s => (s.id === slotId ? { ...s, ...dbPatch } : s)),
    }));
    await supabase.from('class_reminder_slots').update(dbPatch).eq('id', slotId);
  };

  const deleteSlot = async (classId: string, slotId: string) => {
    setSlots(prev => ({ ...prev, [classId]: (prev[classId] ?? []).filter(s => s.id !== slotId) }));
    await supabase.from('class_reminder_slots').delete().eq('id', slotId);
  };

  // ─── Ausnahmen ──────────────────────────────────────────────
  const draftOf = (classId: string) => exDraft[classId] ?? { date: '', status: 'cancelled' as const, note: '' };
  const updateDraft = (classId: string, patch: Partial<{ date: string; status: 'cancelled' | 'postponed'; note: string }>) =>
    setExDraft(prev => ({ ...prev, [classId]: { ...draftOf(classId), ...patch } }));

  const addException = async (classId: string) => {
    const d = draftOf(classId);
    setError('');
    if (!d.date) {
      setError('Bitte ein Datum für die Ausnahme wählen.');
      return;
    }
    const { data, error: err } = await supabase
      .from('class_reminder_exceptions')
      .upsert(
        { class_id: classId, exception_date: d.date, status: d.status, note: d.note.trim() || null, notified_at: null },
        { onConflict: 'class_id,exception_date' }
      )
      .select()
      .single();
    if (err) {
      setError('Ausnahme konnte nicht gespeichert werden.');
      return;
    }
    setExceptions(prev => {
      const rest = (prev[classId] ?? []).filter(e => e.exception_date !== d.date);
      return { ...prev, [classId]: [...rest, data as ClassReminderException].sort((a, b) => a.exception_date.localeCompare(b.exception_date)) };
    });
    updateDraft(classId, { date: '', note: '' });
    setOpenEx(prev => ({ ...prev, [classId]: false }));
  };

  const deleteException = async (classId: string, exId: string) => {
    setExceptions(prev => ({ ...prev, [classId]: (prev[classId] ?? []).filter(e => e.id !== exId) }));
    await supabase.from('class_reminder_exceptions').delete().eq('id', exId);
  };

  const query = search.trim().toLowerCase();
  const filteredClasses = query
    ? classes.filter(c =>
        c.title.toLowerCase().includes(query) || (c.level ?? '').toLowerCase().includes(query)
      )
    : classes;

  const summaryOf = (classId: string) => {
    const cfg = configs[classId];
    const count = (slots[classId] ?? []).length;
    return {
      hasWebhook: Boolean(cfg?.webhook_url),
      slotCount: count,
    };
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">Portal</p>
        <h2 className="text-3xl font-bold text-[#1f1f1f]">Live-Erinnerungen</h2>
        <p className="text-[#6b6b6b] mt-1 max-w-2xl">
          Pro Klasse eine Discord-Webhook-URL und einen Wochenplan hinterlegen. Das System
          pingt automatisch kurz vor jeder Einheit den Channel. Fällt eine Einheit aus oder
          verschiebt sich, trägst du unten eine Ausnahme ein – sie wird automatisch gemeldet.
        </p>
      </div>

      <div className={cardClass + ' p-5 text-sm text-[#5f5148] leading-relaxed'}>
        <p className="font-semibold text-[#1f1f1f] mb-1">So bekommst du die Webhook-URL:</p>
        Discord → betreffender Channel → <em>Bearbeiten</em> → <em>Integrationen</em> →
        <em> Webhooks</em> → <em>Neuer Webhook</em> → <em>Webhook-URL kopieren</em>.
        <br />
        <span className="font-semibold text-[#1f1f1f]">Erwähnung/Ping:</span> Nur{' '}
        <code>@everyone</code> und <code>@here</code> pingen als Text. Ein Rollenname wie{' '}
        <code>@Schüler</code> pingt <em>nicht</em> – für eine Rolle brauchst du die Rollen-ID im
        Format <code>&lt;@&amp;ROLLEN_ID&gt;</code> (Discord → Servereinstellungen → Rollen →
        „Rollen-ID kopieren", Entwicklermodus muss an sein).
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className={cardClass + ' p-10 text-center text-[#6b6b6b]'}>Wird geladen…</div>
      ) : classes.length === 0 ? (
        <div className={cardClass + ' p-10 text-center text-[#6b6b6b]'}>Keine aktiven Klassen vorhanden.</div>
      ) : (
        <>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9b9490]" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Klasse suchen…"
              className={inputClass + ' pl-11'}
            />
          </div>

          {filteredClasses.length === 0 ? (
            <div className={cardClass + ' p-8 text-center text-[#6b6b6b]'}>
              Keine Klasse gefunden für „{search}".
            </div>
          ) : (
        <div className="space-y-4">
          {filteredClasses.map(c => {
            const cfg = cfgOf(c.id);
            const classSlots = slots[c.id] ?? [];
            const classExceptions = exceptions[c.id] ?? [];
            const draft = draftOf(c.id);
            const isOpen = Boolean(expanded[c.id]);
            const summary = summaryOf(c.id);
            return (
              <div key={c.id} className={cardClass + ' p-5'}>
                <div className="flex items-center justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setExpanded(prev => ({ ...prev, [c.id]: !prev[c.id] }))}
                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-[#970000] shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-[#970000] shrink-0" />
                    )}
                    <Bell className="h-4 w-4 text-[#970000] shrink-0" />
                    <h3 className="font-bold text-[#1f1f1f] truncate">{c.title}</h3>
                    {c.level && <span className="text-xs text-[#6b6b6b] shrink-0">({c.level})</span>}
                    {!isOpen && (
                      <span className="ml-2 text-xs text-[#9b9490] truncate">
                        {summary.hasWebhook ? '· Webhook ✓' : '· Webhook fehlt'}
                        {summary.slotCount > 0 ? ` · ${summary.slotCount} Termin${summary.slotCount === 1 ? '' : 'e'}` : ' · kein Termin'}
                        {!cfg.is_enabled ? ' · inaktiv' : ''}
                      </span>
                    )}
                  </button>
                  <label className="flex items-center gap-2 text-sm text-[#5f5148] cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={cfg.is_enabled}
                      onChange={e => toggleEnabled(c.id, e.target.checked)}
                      className="h-4 w-4 accent-[#970000]"
                    />
                    Aktiv
                  </label>
                </div>

                {isOpen && (
                <div className="mt-4">
                {/* Webhook-Konfiguration */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Discord-Webhook-URL</label>
                    <input
                      type="url"
                      value={cfg.webhook_url}
                      onChange={e => updateCfg(c.id, { webhook_url: e.target.value })}
                      className={inputClass}
                      placeholder="https://discord.com/api/webhooks/…"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Erwähnung (optional)</label>
                      <input
                        type="text"
                        value={cfg.mention}
                        onChange={e => updateCfg(c.id, { mention: e.target.value })}
                        className={inputClass}
                        placeholder="@everyone"
                      />
                      <p className="text-xs text-[#9b9490] mt-1">
                        <code>@everyone</code> / <code>@here</code> oder für eine Rolle{' '}
                        <code>&lt;@&amp;ID&gt;</code>. Ein Rollenname pingt nicht.
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Vorlauf (Minuten)</label>
                      <input
                        type="number"
                        min={1}
                        max={240}
                        value={cfg.lead_minutes}
                        onChange={e => updateCfg(c.id, { lead_minutes: Math.max(1, Math.min(240, Number(e.target.value) || 15)) })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => saveConfig(c.id)} disabled={savingId === c.id} className={primaryBtn}>
                      <Check className="h-4 w-4 mr-1" />
                      {savingId === c.id ? 'Speichert…' : 'Webhook speichern'}
                    </button>
                    <button
                      type="button"
                      onClick={() => sendTest(c.id)}
                      disabled={testState[c.id] === 'sending'}
                      className={secondaryBtn}
                    >
                      <Send className="h-4 w-4 mr-1" />
                      {testState[c.id] === 'sending' ? 'Sendet…' : 'Testnachricht senden'}
                    </button>
                    {savedId === c.id && <span className="text-sm text-green-700">Gespeichert ✓</span>}
                    {testState[c.id] === 'ok' && <span className="text-sm text-green-700">Ausgelöst ✓ – erscheint gleich in Discord</span>}
                    {testState[c.id] === 'err' && <span className="text-sm text-red-600">Fehlgeschlagen – siehe Meldung oben</span>}
                  </div>
                </div>

                {/* Wochenplan */}
                <div className="mt-6 pt-5 border-t border-[#eadfd6]">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-[#1f1f1f]">Wochenplan (wann findet die Einheit statt?)</p>
                    <button onClick={() => addSlot(c.id)} className={secondaryBtn}>
                      <Plus className="h-3.5 w-3.5 mr-1" />Termin hinzufügen
                    </button>
                  </div>
                  {classSlots.length === 0 ? (
                    <p className="text-sm text-[#9b9490]">Noch kein Termin – ohne Wochenplan wird nicht erinnert.</p>
                  ) : (
                    <div className="space-y-2">
                      {classSlots.map(s => (
                        <div key={s.id} className="flex items-center gap-3">
                          <select
                            value={s.weekday}
                            onChange={e => updateSlot(c.id, s.id, { weekday: Number(e.target.value) })}
                            className={inputClass + ' max-w-[180px]'}
                          >
                            {WEEKDAYS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                          </select>
                          <input
                            type="time"
                            value={s.start_time?.slice(0, 5) ?? ''}
                            onChange={e => updateSlot(c.id, s.id, { start_time: e.target.value })}
                            className={inputClass + ' max-w-[140px]'}
                          />
                          <button
                            onClick={() => deleteSlot(c.id, s.id)}
                            className="p-2 rounded-xl text-red-500 hover:bg-red-50"
                            aria-label="Termin löschen"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Ausfall / Verschiebung – nur bei Bedarf, spontan */}
                <div className="mt-6 pt-5 border-t border-[#eadfd6]">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-sm font-semibold text-[#1f1f1f] flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-[#970000]" />
                      Ausfall / Verschiebung <span className="font-normal text-[#9b9490]">(nur wenn nötig)</span>
                    </p>
                    {!openEx[c.id] && (
                      <button onClick={() => setOpenEx(prev => ({ ...prev, [c.id]: true }))} className={secondaryBtn}>
                        <Plus className="h-3.5 w-3.5 mr-1" />Termin ausfallen lassen
                      </button>
                    )}
                  </div>

                  {/* Bereits eingetragene Ausnahmen (falls vorhanden) */}
                  {classExceptions.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {classExceptions.map(ex => (
                        <div key={ex.id} className="flex items-center gap-2 text-sm bg-[#faf8f6] rounded-xl px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ex.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                            {ex.status === 'cancelled' ? 'Fällt aus' : 'Verschoben'}
                          </span>
                          <span className="text-[#5f5148]">
                            {new Date(ex.exception_date).toLocaleDateString('de-DE')}
                            {ex.note ? ` – ${ex.note}` : ''}
                          </span>
                          <span className="ml-auto text-xs text-[#9b9490]">
                            {ex.notified_at ? 'gemeldet ✓' : 'wird gemeldet…'}
                          </span>
                          <button
                            onClick={() => deleteException(c.id, ex.id)}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                            aria-label="Ausnahme entfernen"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Eingabeformular – erst auf Klick sichtbar */}
                  {openEx[c.id] ? (
                    <div className="bg-[#faf8f6] rounded-2xl p-4">
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-[#6b6b6b] mb-1">Datum</label>
                          <input
                            type="date"
                            value={draft.date}
                            onChange={e => updateDraft(c.id, { date: e.target.value })}
                            className={inputClass + ' max-w-[170px]'}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-[#6b6b6b] mb-1">Art</label>
                          <select
                            value={draft.status}
                            onChange={e => updateDraft(c.id, { status: e.target.value as 'cancelled' | 'postponed' })}
                            className={inputClass + ' max-w-[170px]'}
                          >
                            <option value="cancelled">Fällt aus</option>
                            <option value="postponed">Verschoben / später</option>
                          </select>
                        </div>
                        <div className="flex-1 min-w-[180px]">
                          <label className="block text-xs font-semibold text-[#6b6b6b] mb-1">Hinweis (optional)</label>
                          <input
                            type="text"
                            value={draft.note}
                            onChange={e => updateDraft(c.id, { note: e.target.value })}
                            className={inputClass}
                            placeholder={draft.status === 'postponed' ? 'z. B. Beginnt 15 Min später (19:15)' : 'z. B. Grund des Ausfalls'}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => addException(c.id)} className={primaryBtn}>
                          <Plus className="h-4 w-4 mr-1" />Eintragen &amp; melden
                        </button>
                        <button onClick={() => setOpenEx(prev => ({ ...prev, [c.id]: false }))} className={secondaryBtn}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  ) : (
                    classExceptions.length === 0 && (
                      <p className="text-sm text-[#9b9490]">
                        Trag hier nur dann etwas ein, wenn eine konkrete Einheit ausfällt oder sich verschiebt.
                      </p>
                    )
                  )}
                </div>
                </div>
                )}
              </div>
            );
          })}
        </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReminderSettingsManager;
